"use client";

import { AnimatePresence, motion, useMotionValue, useTransform } from "framer-motion";
import { type FormEvent, type MouseEvent, useEffect, useState } from "react";
import Magnetic from "./ui/Magnetic";

type InquiryKind = "Project" | "Full-time Role" | "Consulting" | "Quick Question";

const inquiryOptions: {
  kind: InquiryKind;
  title: string;
  detail: string;
  iconClass: string;
}[] = [
  {
    kind: "Project",
    title: "Project Build",
    detail: "For founders, teams, or businesses planning a new product or platform.",
    iconClass: "fi fi-rr-briefcase",
  },
  {
    kind: "Full-time Role",
    title: "Full-time Role",
    detail: "For recruiters and hiring managers exploring long-term engineering roles.",
    iconClass: "fi fi-rr-badge",
  },
  {
    kind: "Consulting",
    title: "Consulting",
    detail: "For audits, architecture direction, performance, and technical strategy.",
    iconClass: "fi fi-rr-chart-tree-map",
  },
  {
    kind: "Quick Question",
    title: "Quick Question",
    detail: "For a short introduction before deciding the right next step.",
    iconClass: "fi fi-rr-comment-alt",
  },
];

export default function CTA() {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [budget, setBudget] = useState("");
  const [message, setMessage] = useState("");
  const [inquiryKind, setInquiryKind] = useState<InquiryKind>("Project");
  const [briefReady, setBriefReady] = useState(false);
  
  const [copiedBrief, setCopiedBrief] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [currentTime, setCurrentTime] = useState("");

  useEffect(() => {
    // Dynamic Timezone clock set to IST (UTC +5:30)
    const updateTime = () => {
      const options: Intl.DateTimeFormatOptions = {
        timeZone: "Asia/Kolkata",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false
      };
      setCurrentTime(new Date().toLocaleTimeString("en-US", options));
    };
    updateTime();
    const timer = setInterval(updateTime, 1000);
    return () => clearInterval(timer);
  }, []);

  function handleMouseMove({ currentTarget, clientX, clientY }: MouseEvent) {
    const { left, top } = currentTarget.getBoundingClientRect();
    mouseX.set(clientX - left);
    mouseY.set(clientY - top);
  }

  useEffect(() => {
    if (!copiedBrief) return;
    const timeout = window.setTimeout(() => setCopiedBrief(false), 1800);
    return () => window.clearTimeout(timeout);
  }, [copiedBrief]);

  useEffect(() => {
    if (!copiedEmail) return;
    const timeout = window.setTimeout(() => setCopiedEmail(false), 1800);
    return () => window.clearTimeout(timeout);
  }, [copiedEmail]);

  function selectIntent(kind: InquiryKind) {
    setInquiryKind(kind);
    setModalOpen(false);
    setBriefReady(false);

    const section = document.getElementById("contact");
    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  function handlePrepareBrief(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBriefReady(true);
  }

  const inquiryText = [
    `Inquiry Type: ${inquiryKind}`,
    `Name: ${name || "Not provided"}`,
    `Email: ${email || "Not provided"}`,
    `Company / Role: ${company || "Not provided"}`,
    `Budget / Scope: ${budget || "Not provided"}`,
    "",
    "Message:",
    message || "Not provided",
  ].join("\n");

  const whatsappHref = `https://wa.me/919817044885?text=${encodeURIComponent(inquiryText)}`;
  const emailHref = `mailto:contact@amritsharma.dev?subject=${encodeURIComponent(
    `${inquiryKind} inquiry for Amrit Sharma`
  )}&body=${encodeURIComponent(inquiryText)}`;

  async function copyBrief() {
    await navigator.clipboard.writeText(inquiryText);
    setCopiedBrief(true);
  }

  async function copyEmailAddress() {
    await navigator.clipboard.writeText("contact@amritsharma.dev");
    setCopiedEmail(true);
  }

  return (
    <>
      <section id="contact" className="section-pad relative z-10 overflow-hidden scroll-mt-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 30 }}
            whileInView={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true }}
            onMouseMove={handleMouseMove}
            className="group relative rounded-[2.5rem] border border-indigo-500/20 bg-gradient-to-br from-indigo-500/10 via-slate-500/5 to-transparent p-5 sm:p-6 md:p-10 overflow-hidden shadow-2xl"
          >
            {/* Spotlight hover effect */}
            <motion.div
              className="pointer-events-none absolute -inset-px rounded-[3rem] opacity-0 transition duration-300 group-hover:opacity-100"
              style={{
                background: useTransform(
                  [mouseX, mouseY],
                  ([x, y]) =>
                    `radial-gradient(800px circle at ${x}px ${y}px, rgba(99, 102, 241, 0.15), transparent 80%)`
                ),
              }}
            />

            <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500/10 rounded-full blur-[120px] -mr-48 -mt-48 animate-pulse" />
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-500/10 rounded-full blur-[120px] -ml-48 -mb-48 animate-pulse" />

            <div className="relative z-10 grid gap-10 lg:grid-cols-[1.1fr_0.9fr]">
              {/* Text specifications Left */}
              <div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.8 }}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-white/5 border border-white/10 text-indigo-400 text-[10px] font-black uppercase tracking-[0.22em] mb-6 shadow-xl"
                >
                  <i className="fi fi-rr-comments text-[10px]" />
                  Ingress Connection Channels
                </motion.div>

                <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white mb-4 leading-none font-[var(--font-poppins)] tracking-tighter">
                  Contact node
                  <br />
                  <span className="gradient-text">no friction gateway.</span>
                </h2>

                <p className="text-gray-400 text-sm md:text-base mb-8 leading-relaxed font-medium max-w-lg">
                  Submit your brief specs using the inputs. I am available for remote operations, contract assignments, core open-source coordination, or technical presentations.
                </p>

                {/* Grid stats parameters */}
                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="bg-white/5 border border-white/5 p-4 rounded-2xl flex flex-col font-mono">
                    <span className="text-[9px] text-gray-500 uppercase tracking-widest block mb-1">Local Node Time</span>
                    <span className="text-sm font-bold text-indigo-400">{currentTime || "UTC +05:30"} IST</span>
                  </div>
                  <div className="bg-white/5 border border-white/5 p-4 rounded-2xl flex flex-col font-mono">
                    <span className="text-[9px] text-gray-500 uppercase tracking-widest block mb-1">Service Latency</span>
                    <span className="text-sm font-bold text-emerald-400">Avg: ~2 Hours</span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
                  <Magnetic>
                    <button
                      type="button"
                      onClick={() => setModalOpen(true)}
                      className="btn-gradient min-w-[200px] flex items-center justify-center gap-3 px-6 py-3.5 rounded-2xl font-black uppercase tracking-[0.18em] shadow-2xl shadow-indigo-500/40 text-[10px] sm:text-xs transition-transform active:scale-95"
                    >
                      <i className="fi fi-rr-comment-dots text-base" />
                      <span>Start Guided Brief</span>
                    </button>
                  </Magnetic>

                  <Magnetic>
                    <button
                      onClick={copyEmailAddress}
                      className="glass min-w-[200px] flex items-center justify-center gap-3 px-6 py-3.5 rounded-2xl border border-white/10 text-white font-black uppercase tracking-[0.18em] text-[10px] sm:text-xs hover:bg-white/10 transition-all active:scale-95 cursor-pointer"
                    >
                      <i className="fi fi-rr-envelope text-base" />
                      <span>{copiedEmail ? "Copied address" : "Copy email Node"}</span>
                    </button>
                  </Magnetic>
                </div>
              </div>

              {/* Form Input builder Right */}
              <div className="rounded-[2.5rem] border border-white/10 bg-white/[0.02] p-6 md:p-8 flex flex-col justify-between">
                <div className="flex items-center justify-between gap-3 mb-6">
                  <div>
                    <p className="text-[9px] font-black uppercase tracking-[0.2em] text-indigo-400 mb-1">
                      Brief Parameters
                    </p>
                    <h3 className="text-xl font-black text-white font-[var(--font-poppins)] tracking-tight">
                      Build Specifications
                    </h3>
                  </div>
                  <div className="rounded-xl border border-white/10 bg-white/5 px-2.5 py-1 text-[9px] font-mono uppercase text-indigo-300">
                    {inquiryKind}
                  </div>
                </div>

                <form onSubmit={handlePrepareBrief} className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <input
                      required
                      value={name}
                      onChange={(event) => setName(event.target.value)}
                      placeholder="Your name"
                      className="w-full rounded-2xl border border-white/5 bg-white/5 px-4 py-3.5 text-xs text-white placeholder:text-gray-500 outline-none transition-all focus:border-indigo-400/50 focus:bg-indigo-500/[0.02] focus:shadow-[0_0_15px_rgba(99,102,241,0.1)]"
                    />
                    <input
                      required
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      type="email"
                      placeholder="Email address"
                      className="w-full rounded-2xl border border-white/5 bg-white/5 px-4 py-3.5 text-xs text-white placeholder:text-gray-500 outline-none transition-all focus:border-indigo-400/50 focus:bg-indigo-500/[0.02] focus:shadow-[0_0_15px_rgba(99,102,241,0.1)]"
                    />
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <input
                      value={company}
                      onChange={(event) => setCompany(event.target.value)}
                      placeholder="Company or Role"
                      className="w-full rounded-2xl border border-white/5 bg-white/5 px-4 py-3.5 text-xs text-white placeholder:text-gray-500 outline-none transition-all focus:border-indigo-400/50 focus:bg-indigo-500/[0.02] focus:shadow-[0_0_15px_rgba(99,102,241,0.1)]"
                    />
                    <input
                      value={budget}
                      onChange={(event) => setBudget(event.target.value)}
                      placeholder="Budget or Scope"
                      className="w-full rounded-2xl border border-white/5 bg-white/5 px-4 py-3.5 text-xs text-white placeholder:text-gray-500 outline-none transition-all focus:border-indigo-400/50 focus:bg-indigo-500/[0.02] focus:shadow-[0_0_15px_rgba(99,102,241,0.1)]"
                    />
                  </div>

                  <textarea
                    required
                    value={message}
                    onChange={(event) => setMessage(event.target.value)}
                    placeholder="Provide details on systems specs you want to improve, build, or deploy..."
                    rows={4}
                    className="w-full rounded-[1.5rem] border border-white/5 bg-white/5 px-4 py-3.5 text-xs text-white placeholder:text-gray-500 outline-none transition-all focus:border-indigo-400/50 focus:bg-indigo-500/[0.02] focus:shadow-[0_0_15px_rgba(99,102,241,0.1)] resize-none"
                  />

                  <button
                    type="submit"
                    className="btn-gradient w-full flex items-center justify-center gap-3 px-6 py-3.5 rounded-2xl font-black uppercase tracking-[0.18em] text-[10px] sm:text-xs shadow-xl shadow-indigo-500/30"
                  >
                    <i className="fi fi-rr-check text-sm" />
                    <span>Compile Inquiry Brief</span>
                  </button>
                </form>

                <AnimatePresence>
                  {briefReady && (
                    <motion.div
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 16 }}
                      className="mt-6 rounded-2xl border border-white/10 bg-black/40 p-4 font-mono text-[10px]"
                    >
                      <div className="flex items-start justify-between gap-3 mb-4">
                        <div>
                          <p className="font-bold text-emerald-400 uppercase tracking-widest mb-1">
                            Inquiry Payload Ready
                          </p>
                          <p className="text-gray-400 leading-normal">
                            Parameters formatted. Select deployment target channel:
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={copyBrief}
                          className="rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-[9px] font-bold text-white hover:bg-white/10 transition-colors uppercase tracking-wider cursor-pointer"
                        >
                          {copiedBrief ? "Copied" : "Copy Payload"}
                        </button>
                      </div>

                      <div className="grid gap-2.5 sm:grid-cols-3">
                        <a
                          href={whatsappHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="rounded-xl border border-white/5 bg-white/5 py-2.5 text-center font-bold text-white hover:border-green-400/30 hover:bg-green-500/10 transition-all uppercase tracking-wider"
                        >
                          WhatsApp Node
                        </a>
                        <a
                          href="https://www.linkedin.com/in/amrit-sharma-b11b88124"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="rounded-xl border border-white/5 bg-white/5 py-2.5 text-center font-bold text-white hover:border-blue-400/30 hover:bg-blue-500/10 transition-all uppercase tracking-wider"
                        >
                          LinkedIn
                        </a>
                        <a
                          href={emailHref}
                          className="rounded-xl border border-white/5 bg-white/5 py-2.5 text-center font-bold text-white hover:border-indigo-400/30 hover:bg-indigo-500/10 transition-all uppercase tracking-wider"
                        >
                          Direct Email
                        </a>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Guided Start Modal */}
      <AnimatePresence>
        {modalOpen && (
          <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-md px-4 sm:px-6 py-6 flex items-center justify-center">
            {/* Backdrop close layer */}
            <div className="absolute inset-0" onClick={() => setModalOpen(false)} />
            
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 30, scale: 0.96 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="w-full max-w-4xl rounded-[2.5rem] border border-white/10 bg-[#060411]/95 p-6 md:p-8 shadow-2xl relative z-10"
            >
              <div className="flex items-start justify-between gap-3 mb-8">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-[0.18em] text-indigo-400 mb-1">
                    Guided Ingress Selector
                  </p>
                  <h3 className="text-2xl sm:text-3xl font-black text-white font-[var(--font-poppins)] tracking-tight">
                    Select inquiry type parameters:
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="w-10 h-10 rounded-2xl border border-white/10 bg-white/5 text-white hover:bg-white/10 transition-colors flex items-center justify-center cursor-pointer"
                >
                  <i className="fi fi-rr-cross text-xs" />
                </button>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                {inquiryOptions.map((option) => (
                  <button
                    key={option.kind}
                    type="button"
                    onClick={() => selectIntent(option.kind)}
                    className="text-left rounded-[1.7rem] border border-white/5 bg-white/[0.02] p-5 hover:border-indigo-400/40 hover:bg-white/[0.04] transition-all cursor-pointer"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-indigo-300">
                        <i className={`${option.iconClass} text-base`} />
                      </div>
                      <div>
                        <h4 className="text-[11px] font-black uppercase tracking-[0.16em] text-white mb-1">
                          {option.title}
                        </h4>
                        <p className="text-xs text-gray-400 leading-relaxed font-medium">{option.detail}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
